import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Table } from "reactstrap";
import { toast } from "react-toastify";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../../utils/site";

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/payments"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Failed to fetch payments.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async (id) => {
    try {
      await axios.put(
        getApiUrl(`/api/payments/${id}/verify`),
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Payment verified successfully.");
      fetchPayments();
    } catch (error) {
      toast.error("Failed to verify payment.");
    }
  };

  const handleRefundPayment = async (id) => {
    if (!window.confirm("Are you sure you want to refund this payment?")) return;

    try {
      await axios.put(
        getApiUrl(`/api/payments/${id}/refund`),
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Payment refunded successfully.");
      fetchPayments();
    } catch (error) {
      toast.error("Failed to refund payment.");
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axios.put(
        getApiUrl(`/api/payments/${id}`),
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Payment status updated.");
      fetchPayments();
    } catch (error) {
      toast.error("Failed to update payment status.");
    }
  };

  const summary = useMemo(() => {
    const total = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const success = payments.filter((payment) => String(payment.status || "").toLowerCase() === "success").length;
    const pending = payments.filter((payment) => String(payment.status || "").toLowerCase() === "pending").length;
    return { total, success, pending };
  }, [payments]);

  return (
    <AdminPageShell
      eyebrow="Finance operations"
      title="Payment Management"
      description="Verify payments, update transaction status, download receipts, and keep finance operations consistent."
      actions={
        <button type="button" className="roha-admin-navbar__chip" onClick={fetchPayments}>
          Refresh payments
        </button>
      }
    >
      <section className="roha-admin-card-grid">
        <article className="roha-admin-stat roha-admin-stat--gold">
          <div className="roha-admin-stat__body">
            <span>Total value</span>
            <strong>ETB {summary.total.toFixed(0)}</strong>
            <p>Recorded payment amount.</p>
          </div>
        </article>
        <article className="roha-admin-stat roha-admin-stat--emerald">
          <div className="roha-admin-stat__body">
            <span>Successful</span>
            <strong>{summary.success}</strong>
            <p>Payments marked as successful.</p>
          </div>
        </article>
        <article className="roha-admin-stat roha-admin-stat--ink">
          <div className="roha-admin-stat__body">
            <span>Pending</span>
            <strong>{summary.pending}</strong>
            <p>Payments awaiting review.</p>
          </div>
        </article>
        <article className="roha-admin-stat roha-admin-stat--slate">
          <div className="roha-admin-stat__body">
            <span>Records</span>
            <strong>{payments.length}</strong>
            <p>Total finance rows loaded.</p>
          </div>
        </article>
      </section>

      <AdminSurface title="Payment control desk" subtitle={`${payments.length} payment records`}>
        <div className="roha-admin-table-wrap">
          <Table responsive className="roha-admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Order ID</th>
                <th>Item</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Receipt</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="roha-admin-empty">Loading payments...</td>
                </tr>
              ) : payments.length ? (
                payments.map((payment, index) => {
                  const status = String(payment.status || "").toLowerCase();
                  return (
                    <tr key={payment.id}>
                      <td data-label="#">{index + 1}</td>
                      <td data-label="Customer">{payment.customer_name || "-"}</td>
                      <td data-label="Order ID">{payment.order_id || "-"}</td>
                      <td data-label="Item">{payment.medicine_name || payment.product_name || "-"}</td>
                      <td data-label="Amount">ETB {payment.amount}</td>
                      <td data-label="Method">{payment.payment_method || payment.payment_gateway || "-"}</td>
                      <td data-label="Status">
                        <select
                          value={payment.status || "pending"}
                          onChange={(event) => handleUpdateStatus(payment.id, event.target.value)}
                          className="form-select form-select-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                        </select>
                      </td>
                      <td data-label="Receipt">
                        {status === "success" || status === "completed" ? (
                          <a href={getApiUrl(`/api/payments/receipt/${payment.id}`)} download target="_blank" rel="noopener noreferrer" className="roha-admin-navbar__chip">
                            Download
                          </a>
                        ) : (
                          <span className="roha-admin-muted">Not available</span>
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className="roha-admin-toolbar">
                          {status === "pending" ? (
                            <Button color="success" size="sm" onClick={() => handleVerifyPayment(payment.id)}>
                              Verify
                            </Button>
                          ) : null}
                          {status === "success" || status === "completed" ? (
                            <Button color="danger" size="sm" onClick={() => handleRefundPayment(payment.id)}>
                              Refund
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="roha-admin-empty">No payments found.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default PaymentManagement;
